import { AnalyticsService, stashLoginMethod } from '@alison/analytics/client';
import { Button } from '@alison/ui';
import { GoogleIcon, MicrosoftIcon } from '@alison/ui/icons';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { TenantLogo } from '@/components/TenantLogo.js';
import { localProdLogin } from '../../env.js';
import { signIn, useSession } from '../../lib/authClient.js';

type Provider = 'google' | 'microsoft';

const providerButtonSx = {
  height: 46,
  borderWidth: 1.5,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  justifyContent: 'center',
  '&:hover': {
    borderWidth: 1.5,
    borderColor: 'primary.main',
    bgcolor: 'background.paper',
  },
} as const;

function LoginPage(): ReactNode {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Provider | null>(null);
  const [sessionExpired, setSessionExpired] = useState(
    () => new URLSearchParams(window.location.search).get('reason') === 'session-expired',
  );

  function dismissSessionExpired(): void {
    setSessionExpired(false);
    void navigate({ to: '/login', replace: true });
  }

  async function onSignIn(provider: Provider): Promise<void> {
    setPending(provider);
    setError(null);
    stashLoginMethod(provider);
    // No session yet on a first-time visit, so exclusion is unknown — only a
    // return visit (e.g. retrying after a failed brand login) has one already.
    const excluded =
      session?.user?.email !== undefined && AnalyticsService.isExcludedEmail(session.user.email);
    if (!excluded) {
      AnalyticsService.track('Auth - Login Started', { method: provider });
    }
    const result = await signIn.social({
      provider,
      callbackURL: `${window.location.origin}/login/sign-in`,
    });
    if (result.error) {
      const reason = result.error.message ?? 'Sign in failed';
      setPending(null);
      setError(reason);
      if (!excluded) {
        AnalyticsService.track('Auth - Login Failed', {
          stage: 'sso',
          reason,
          method: provider,
        });
      }
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <TenantLogo />
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 5,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              height: 4,
              width: 44,
              borderRadius: 999,
              background: (theme) => theme.gradients.primary,
              mx: 'auto',
              mb: 2.5,
            }}
          />
          <Typography variant="h2" align="center">
            Sign in
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
            Use your work account to continue.
          </Typography>

          <Stack spacing={1.5}>
            <Button
              variant="outlined"
              fullWidth
              disabled={pending !== null}
              startIcon={<GoogleIcon />}
              onClick={() => void onSignIn('google')}
              data-testid="sso-google"
              sx={providerButtonSx}
            >
              Continue with Google
            </Button>
            <Button
              variant="outlined"
              fullWidth
              disabled={pending !== null}
              startIcon={<MicrosoftIcon />}
              onClick={() => void onSignIn('microsoft')}
              data-testid="sso-microsoft"
              sx={providerButtonSx}
            >
              Continue with Microsoft
            </Button>
            {error !== null && <Alert severity="error">{error}</Alert>}
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 2.5 }}
          >
            Only business accounts can sign in.
          </Typography>
        </Paper>
      </Box>

      <Snackbar
        open={sessionExpired}
        autoHideDuration={3000}
        onClose={() => dismissSessionExpired()}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => dismissSessionExpired()}
          data-testid="session-expired-notice"
        >
          Your session expired. Please sign in again.
        </Alert>
      </Snackbar>
    </Box>
  );
}

export const Route = createFileRoute('/login/')({
  beforeLoad: () => {
    if (localProdLogin) throw redirect({ to: '/login/sign-in', replace: true });
  },
  component: LoginPage,
});
